import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { getLocalApiUrl } from '../../config/api'
import FormFillFields, { FormLocaleSwitch } from './FormFillFields'
import FormBrandingHeader, { FormTenantLogo, formSubmitButtonStyle } from './FormBrandingHeader'
import {
  DEFAULT_LOCALE,
  afterSubmitRedirect,
  formCardMaxWidth,
  formPageBackgroundIsDark,
  formPageStyle,
  normalizeLocales,
  resolveConfirmMessage,
  resolveFormDisplayTitle,
  resolveFormSubmitLabel,
} from './formBuilderModel'

export default function PublicFormFill() {
  const { slug } = useParams()
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const [bundle, setBundle] = useState(null)
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [ref, setRef] = useState('')
  const [busy, setBusy] = useState(false)
  const [locale, setLocale] = useState(DEFAULT_LOCALE)
  const draftTimerRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!token) {
        setError('This link is missing a token.')
        return
      }
      try {
        const res = await fetch(getLocalApiUrl(`/api/public/forms?token=${encodeURIComponent(token)}`))
        const j = await res.json()
        if (cancelled) return
        if (!res.ok || !j.valid) {
          setError(j.error || 'This link is invalid or has expired.')
          return
        }
        setBundle(j.data)
        const locales = normalizeLocales(j.data.version?.settings)
        setLocale(locales[0] || DEFAULT_LOCALE)
        const existing = j.data.draft?.answers || {}
        const { schemaVersion, ...rest } = existing
        setAnswers(rest)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to open form')
      }
    }
    load()
    return () => { cancelled = true }
  }, [token, slug])

  useEffect(() => {
    if (!bundle || done || !token) return undefined
    if (['accepted', 'cancelled'].includes(bundle?.assignment?.status) || bundle?.submission) return undefined
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current)
    draftTimerRef.current = setTimeout(async () => {
      try {
        await fetch(getLocalApiUrl('/api/public/forms/draft'), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, answers }),
        })
      } catch (_) {
        /* non-blocking autosave */
      }
    }, 1500)
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current)
    }
  }, [answers, bundle, done, token])

  const submit = async () => {
    setBusy(true)
    setError('')
    setErrors({})
    try {
      const res = await fetch(getLocalApiUrl('/api/public/forms'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, answers }),
      })
      const j = await res.json()
      if (res.status === 422 && j.errors) {
        setErrors(Object.fromEntries(j.errors.map((e) => [e.fieldId, e.message])))
        throw new Error(j.error || 'Please fix the highlighted fields')
      }
      if (!res.ok || !j.success) throw new Error(j.error || 'Submit failed')
      const nextRef = j.data.submission?.ref || ''
      setRef(nextRef)
      setDone(true)
      const dest = afterSubmitRedirect(bundle?.version?.settings, nextRef)
      if (dest) window.setTimeout(() => { window.location.assign(dest) }, 1600)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const settings = bundle?.version?.settings
  const branding = bundle?.branding
  const cardMax = formCardMaxWidth(settings)
  const darkPage = !!(bundle?.version && formPageBackgroundIsDark(settings, branding))

  if (!token) {
    return (
      <div className="min-h-screen py-10 px-4 bg-slate-50">
        <div className="mx-auto max-w-lg rounded-2xl border border-red-200 bg-white p-6 text-sm text-red-700">
          This form link is missing a token. Open the link from the email again.
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-10 px-4" style={formPageStyle(settings, branding)}>
      <div className="mx-auto" style={{ maxWidth: cardMax, width: '100%' }}>
        <FormTenantLogo settings={settings} branding={branding} darkPage={darkPage} />
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {bundle?.version ? (
            <FormLocaleSwitch settings={settings} locale={locale} onChange={setLocale} className="mb-4" />
          ) : null}
          <FormBrandingHeader
            formName={resolveFormDisplayTitle(settings, bundle?.assignment?.formName || slug, locale)}
            subjectLabel={bundle?.assignment?.subjectLabel}
          />
          {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
          {done ? (
            <div className="rounded-lg bg-teal-50 border border-teal-200 p-4 text-teal-900">
              {resolveConfirmMessage(settings, {
                locale,
                answers,
                definition: bundle?.version?.definition,
                formName: bundle?.assignment?.formName,
                ref,
              })}
              {ref ? ` — reference ${ref}` : ''}
              {afterSubmitRedirect(settings, ref) ? <p className="mt-2 text-sm text-teal-800">Redirecting…</p> : null}
            </div>
          ) : bundle?.version ? (
            <>
              <FormFillFields
                definition={bundle.version.definition}
                settings={bundle.version.settings}
                locale={locale}
                answers={answers}
                onChange={(id, v) => setAnswers((prev) => ({ ...prev, [id]: v }))}
                errors={errors}
                uploadFile={async (file) => {
                  const body = new FormData()
                  body.append('file', file)
                  const res = await fetch(getLocalApiUrl(`/api/public/forms/file?token=${encodeURIComponent(token)}`), {
                    method: 'POST',
                    body,
                  })
                  const j = await res.json().catch(() => ({}))
                  if (!res.ok || !j.success) throw new Error(j.error || 'Upload failed. Try again.')
                  return j.data
                }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={submit}
                className="mt-6 w-full rounded-lg border text-white font-semibold py-2.5 disabled:opacity-60"
                style={formSubmitButtonStyle(settings, branding)}
              >
                {busy ? resolveFormSubmitLabel(settings, locale, { busy: true }) : resolveFormSubmitLabel(settings, locale)}
              </button>
            </>
          ) : !error ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
